<template>
  <div class="container-fluid">
    <!-- Filter Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <job-status-multi-ac 
                  v-model="filters.status"
                  label="Job Status"
                  placeholder="Select job status to add..."
                />
              </div>
              <div class="col-md-3 d-flex align-items-end">
                <button 
                  class="btn btn-sm btn-primary w-100" 
                  @click="runReport"
                  :disabled="loading || filters.status.length === 0"
                >
                  <i class="fa fa-play me-2"></i>
                  {{ loading ? 'Loading...' : 'Run Report' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Finding stalled jobs...</p>
    </div>

    <!-- Report Results -->
    <div v-if="stalledJobs.length > 0 && !loading" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="mb-0">
                <i class="fa fa-pause-circle me-2 text-warning"></i>
                Stalled Jobs ({{ stalledJobs.length }})
              </h5>
              <div class="text-muted">
                Jobs without follow-up tasks or assignments
              </div>
            </div>

            <!-- Stalled Jobs Table -->
            <div class="table-responsive">
              <table class="table table-bordered table-hover table-sm">
                <thead class="table-light">
                  <tr>
                    <th style="width: 80px;">Job ID</th>
                    <th style="width: 200px;">Client</th>
                    <th>Description</th>
                    <th style="width: 120px;">Status</th>
                    <th style="width: 120px;">Created</th>
                    <th style="width: 120px;">Last Updated</th>
                    <th style="width: 100px;">Priority</th>
                    <th style="width: 80px;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="job in stalledJobs" :key="job.Id" class="table-warning">
                    <td>
                      <a href="#" @click.prevent="openJobModal(job.Id)" class="text-decoration-none fw-bold">
                        #{{ job.Id }}
                      </a>
                    </td>
                    <td>{{ job.Contact?.Company || 'Unknown' }}</td>
                    <td>
                      <div class="text-truncate" style="max-width: 400px;" :title="job.RequestDetailedDescription || job.TaskDetail">
                        {{ job.RequestDetailedDescription || job.TaskDetail || 'No description' }}
                      </div>
                    </td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(job.RequestStatus)">
                        {{ getJobStatusName(job.RequestStatus) }}
                      </span>
                    </td>
                    <td>{{ formatDate(job.DateCreated) }}</td>
                    <td>{{ formatDate(job.DateModified) }}</td>
                    <td>
                      <span class="badge" :class="getPriorityBadgeClass(job.Priority)">
                        {{ job.Priority || 'None' }}
                      </span>
                    </td>
                    <td class="text-center">
                      <button 
                        class="btn btn-sm btn-outline-primary"
                        @click="openJobModal(job.Id)"
                        title="Open Job"
                      >
                        <i class="fa fa-external-link-alt"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Results Message -->
    <div v-if="stalledJobs.length === 0 && !loading && ranReport" class="row">
      <div class="col-12">
        <div class="alert alert-success">
          <i class="fa fa-check-circle me-2"></i>
          Great! No stalled jobs found. All jobs have follow-up tasks or assignments.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Api from '@/services/ApiService';
import Helper from '@/services/Helper';
import Emitter from '@/services/Emitter';
import Alert from '@/services/Alert';
import { useJobStatusStore } from '@/stores/JobStatusStore';
import JobStatusMultiAc from '@/components/Jobs/JobStatusMultiAc.vue';

const jobStatusStore = useJobStatusStore();

const filters = ref({
  status: []
});

const loading = ref(false);
const ranReport = ref(false);
const stalledJobs = ref([]);

const runReport = async () => {
  try {
    loading.value = true;
    ranReport.value = true;

    // Load all jobs (Requests) with their tasks in one call using /v1/Request/List?v=2
    const jobFilter = {
      Status: filters.value.status,
      IncludeContact: true,
      IncludeTasks: true,  // Include tasks in the response to avoid N+1 queries
      Page: 0,
      Size: 9999
    };

    const jobsResponse = await Api.loadRequests(jobFilter);
    const allJobs = jobsResponse.Data || [];

    // Filter to find jobs without any tasks
    const jobsWithoutTasks = allJobs.filter(job => {
      // Check if job has no tasks or only has empty tasks array
      return !job.Tasks || job.Tasks.length === 0;
    });

    stalledJobs.value = jobsWithoutTasks;
  } catch (error) {
    console.error('Failed to load stalled jobs:', error);
    // Don't show alert - just log error for debugging
  } finally {
    loading.value = false;
  }
};

const formatDate = (date) => {
  if (!date) return '-';
  return Helper.formatDate(new Date(date), 'MM/DD/YYYY');
};

const getJobStatusName = (statusId) => {
  if (!statusId && statusId !== 0) return 'Unknown';
  const status = jobStatusStore.list[statusId];
  return status?.Status || `Status ${statusId}`;
};

const getStatusBadgeClass = (statusId) => {
  const statusMap = {
    0: 'bg-secondary',      // Draft
    1: 'bg-warning',        // In Progress
    2: 'bg-success',        // Approved
    3: 'bg-primary',        // Complete
    4: 'bg-danger',         // Cancelled
  };
  return statusMap[statusId] || 'bg-secondary';
};

const getPriorityBadgeClass = (priority) => {
  const normalizedPriority = (priority || '').toLowerCase();
  const priorityMap = {
    'critical': 'bg-danger',
    'high': 'bg-warning',
    'medium': 'bg-info',
    'low': 'bg-secondary',
    'none': 'bg-light text-dark',
    'schedule': 'bg-light text-dark'
  };
  return priorityMap[normalizedPriority] || 'bg-light text-dark';
};

const openJobModal = (jobId) => {
  if (jobId) {
    Emitter.emit('edit:job', jobId);
  }
};

// Initialize stores on mount
onMounted(async () => {
  await jobStatusStore.load();
});
</script>

<style scoped>
.table-warning {
  --bs-table-bg: #fff3cd;
  --bs-table-hover-bg: #ffecb5;
}
</style>
